import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { inviteAthlete, type InviteAthleteError } from "api-client";
import { FormProvider as Form, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  RadioGroup,
  RadioGroupItem,
} from "ui";

import { formSchema } from "src/invitations/InviteAthleteDialog.shared";
import type {
  FormSchema,
  InviteAthleteDialogContentProps,
} from "src/invitations/InviteAthleteDialog.shared";
import { trpcClient } from "src/trpc";

export function InviteAthleteDialogContent(
  props: InviteAthleteDialogContentProps,
) {
  const { t } = useTranslation();

  const partnershipTypeOptions = [
    {
      value: "IN_PERSON",
      label: t("partnershipType.IN_PERSON"),
    },
    {
      value: "ONLINE",
      label: t("partnershipType.ONLINE"),
    },
    {
      value: "HYBRID",
      label: t("partnershipType.HYBRID"),
    },
  ] as const;

  const inviteClientMutation = useMutation({
    mutationFn: (arg: FormSchema) => {
      if (arg.email) {
        return inviteAthlete({
          body: {
            email: arg.email,
            name: arg.name,
            partnershipType: arg.type,
          },
        }).then((response) => {
          return response.data.data;
        });
      }

      return trpcClient.coach.addAthlete.mutate({
        name: arg.name,
        email: arg.email,
        partnershipType: arg.type,
      });
    },
  });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: props.name ?? "",
      email: "",
      type: "IN_PERSON",
    },
  });

  async function handleSubmit(values: FormSchema) {
    if (inviteClientMutation.isPending) {
      return;
    }

    inviteClientMutation.mutate(values, {
      onSuccess: (response) => {
        if (response.id) {
          props.onInviteSuccess?.({
            id: response.id,
            athlete: response.athlete,
          });
        }

        props.closeModal();
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : (err as InviteAthleteError).error.message;
        form.setError("email", {
          message,
        });
      },
    });
  }

  return (
    <Form {...form}>
      <form
        className="space-y-4 md:space-y-6"
        action="#"
        onSubmit={form.handleSubmit(handleSubmit)}
      >
        <DialogHeader>
          <DialogTitle>{t("inviteAthleteDialog.title")}</DialogTitle>
          <DialogDescription>
            {t("inviteAthleteDialog.description")}
          </DialogDescription>
        </DialogHeader>
        <FormField
          control={form.control}
          name="type"
          render={({ field }) => (
            <FormItem className="space-y-3">
              <FormLabel>
                {t("inviteAthleteDialog.fields.type.label")}
              </FormLabel>
              <FormControl>
                <RadioGroup
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                  className="flex flex-col space-y-1"
                >
                  {partnershipTypeOptions.map((option) => {
                    return (
                      <FormItem className="flex items-center space-x-3 space-y-0">
                        <FormControl>
                          <RadioGroupItem value={option.value} />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {option.label}
                        </FormLabel>
                      </FormItem>
                    );
                  })}
                </RadioGroup>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("inviteAthleteDialog.fields.name.label")}{" "}
                <span className="text-muted-foreground font-normal">
                  ({t("optional")})
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t("inviteAthleteDialog.fields.name.placeholder")}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("inviteAthleteDialog.fields.email.label")}{" "}
                <span className="text-muted-foreground font-normal">
                  ({t("optional")})
                </span>
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder={t(
                    "inviteAthleteDialog.fields.email.placeholder",
                  )}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DialogFooter>
          <Button type="submit" loading={inviteClientMutation.isPending}>
            {t("inviteAthleteDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Form>
  );
}
